/**
 * CombatStatusBar.ts — Combat HUD panel for the bottom of the terminal.
 *
 * Displays combatant HP bars, stance, frostbite, active effects, and round.
 * Adapted from RotaryKnights' combat status display.
 *
 * Layout:
 * ──── COMBAT ──── Round 3 ★ BOSS ★
 *   YOU: Frostblade Fighter  ████████░░ 24/30 HP  ❄ 15/100  [PRESSING]
 *   FOE: Bone Lord           ██████░░░░ 38/60 HP             [DEFENSIVE]
 *   ★ PRESSING — Full attack power, press the advantage!
 */

import type { Terminal } from './Terminal';
import { theme, type ColorName } from './ColorTheme';
import type { CombatantState, EnemyDefinition } from '@core/combat/CombatTypes';
import { getStanceNarrative } from '@core/combat/StanceSystem';

export interface CombatHUDData {
  player: CombatantState;
  enemy: CombatantState;
  enemyDef: EnemyDefinition;
  round: number;
  frostbite: number;
  frostbiteMax: number;
}

export class CombatStatusBar {
  private terminal: Terminal;

  constructor(terminal: Terminal) {
    this.terminal = terminal;
  }

  /** Render the full combat HUD. */
  render(data: CombatHUDData): void {
    const { player, enemy, enemyDef, round, frostbite, frostbiteMax } = data;
    const t = this.terminal;

    // Header
    const bossTag = enemyDef.isBoss ? ` ${theme.colorize('★ BOSS ★', 'yellow')}` : '';
    t.writeLine(
      `  ${theme.colorize('──── COMBAT ────', 'frostBlue')} Round ${theme.colorize(String(round), 'brightWhite')}${bossTag}`,
    );

    // Player line
    const playerHPBar = this.buildHPBar(player.hp, player.hpMax, 10);
    const frostStr = frostbite > 0
      ? `  ${theme.colorize(`Frost ${frostbite}/${frostbiteMax}`, 'frostbiteCyan')}`
      : '';
    const playerStanceStr = theme.colorize(`[${player.stance.toUpperCase()}]`, this.stanceColor(player.stance));
    t.writeLine(
      `  ${theme.colorize('YOU:', 'green')} ${padRight(player.name, 20)} ${playerHPBar} ${padRight(`${player.hp}/${player.hpMax} HP`, 10)}${frostStr}  ${playerStanceStr}`,
    );

    // Enemy line
    const enemyHPBar = this.buildHPBar(enemy.hp, enemy.hpMax, 10);
    const enemyStanceStr = theme.colorize(`[${enemy.stance.toUpperCase()}]`, this.stanceColor(enemy.stance));
    t.writeLine(
      `  ${theme.colorize('FOE:', 'red')} ${padRight(enemy.name, 20)} ${enemyHPBar} ${padRight(`${enemy.hp}/${enemy.hpMax} HP`, 10)}              ${enemyStanceStr}`,
    );

    // Stance narrative
    const stanceNarrative = getStanceNarrative(player.stance);
    t.writeLine(
      `  ${theme.colorize('★', 'yellow')} ${theme.colorize(player.stance.toUpperCase(), this.stanceColor(player.stance))} — ${stanceNarrative}`,
    );
  }

  /** Build a colored HP bar string. */
  private buildHPBar(current: number, max: number, width: number): string {
    const ratio = Math.max(0, Math.min(1, current / max));
    const filled = Math.round(ratio * width);
    const empty = width - filled;

    const color: ColorName = ratio > 0.5 ? 'green' : ratio > 0.25 ? 'yellow' : 'red';

    return theme.colorize('█'.repeat(filled), color) +
           theme.colorize('░'.repeat(empty), 'gray');
  }

  /** Get color for stance display. */
  private stanceColor(stance: string): ColorName {
    switch (stance) {
      case 'pressing': return 'green';
      case 'advantage': return 'frostBlue';
      case 'neutral': return 'white';
      case 'defensive': return 'yellow';
      case 'cornered': return 'red';
      default: return 'white';
    }
  }
}

function padRight(str: string, len: number): string {
  return str.length >= len ? str : str + ' '.repeat(len - str.length);
}
